import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ChangePassword() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const forced = !!user.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("A nova senha deve ter pelo menos 8 caracteres.");
    if (next !== confirm) return toast.error("As senhas não coincidem.");
    setBusy(true);
    try {
      const payload = { new_password: next };
      if (!forced) payload.current_password = current;
      await api.post("/auth/change-password", payload);
      setUser({ ...user, must_change_password: false });
      toast.success("Senha atualizada com sucesso");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-muted/30 px-6" data-testid="change-password-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-primary/10 border border-primary/30 grid place-items-center text-primary">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold tracking-tight">
              {forced ? "Defina sua nova senha" : "Alterar senha"}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {forced
                ? "Este é seu primeiro acesso — crie uma senha permanente."
                : "Atualize sua senha regularmente."}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {!forced && (
            <div className="space-y-2">
              <Label>Senha atual</Label>
              <Input data-testid="cp-current" type="password" required autoComplete="off"
                value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
          )}
          <div className="space-y-2">
            <Label>Nova senha</Label>
            <Input data-testid="cp-new" type="password" required minLength={8} autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)}
              placeholder="mínimo 8 caracteres" />
          </div>
          <div className="space-y-2">
            <Label>Confirmar nova senha</Label>
            <Input data-testid="cp-confirm" type="password" required minLength={8} autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 flex items-start gap-2 text-xs text-primary">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Sua senha é armazenada de forma segura com hash bcrypt. Ninguém, nem mesmo o administrador, pode vê-la.</span>
          </div>
          <Button type="submit" data-testid="cp-submit" disabled={busy}
                  className="w-full h-11 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-medium">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando…</> : "Definir senha"}
          </Button>
        </form>
      </div>
    </div>
  );
}
